import React, { useMemo } from 'react';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getDoctorColor, getInitials } from './calendarUtils';

const UNASSIGNED_KEY = 'unassigned';

export default function TodayByDoctorsModal({
    open,
    onClose,
    events,
    doctors,
    colorMap,
    onEventClick,
}) {
    const { t } = useTranslation();
    const today = dayjs();

    const sections = useMemo(() => {
        const todayEvents = events.filter((e) => dayjs(e.date).isSame(today, 'day'));
        const map = new Map();

        doctors.forEach((d) => map.set(d.id, { doctor: d, items: [] }));
        map.set(UNASSIGNED_KEY, { doctor: null, items: [] });

        todayEvents.forEach((ev) => {
            const key = ev.doctor_id ?? UNASSIGNED_KEY;
            if (!map.has(key)) {
                map.set(key, { doctor: ev.doctor ?? null, items: [] });
            }
            map.get(key).items.push(ev);
        });

        return Array.from(map.entries())
            .filter(([, group]) => group.items.length > 0)
            .sort(([a], [b]) => {
                if (a === UNASSIGNED_KEY) return 1;
                if (b === UNASSIGNED_KEY) return -1;
                return 0;
            });
    }, [events, doctors]);

    const totalToday = sections.reduce((sum, [, g]) => sum + g.items.length, 0);

    return (
        <Modal
            centered
            title={
                <span className="text-sm font-semibold text-slate-800">
                    {t('calendar.todayVisits')} · <span className="tabular-nums">{today.format('DD.MM.YYYY')}</span>
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width="100%"
            style={{ maxWidth: 560 }}
        >
            {totalToday === 0 ? (
                <p className="m-0 py-6 text-center text-sm text-slate-500">{t('calendar.noTodayVisits')}</p>
            ) : (
                <div className="max-h-[65vh] space-y-2.5 overflow-y-auto pr-1">
                    {sections.map(([key, group]) => {
                        const name = group.doctor?.name ?? t('calendar.unassigned');
                        const accent = key === UNASSIGNED_KEY
                            ? '#94a3b8'
                            : getDoctorColor(group.doctor?.id ?? key, colorMap);

                        return (
                            <div key={key} className="overflow-hidden rounded-xl border border-slate-200/90">
                                <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-2.5 py-2">
                                    <span
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                                        style={{ backgroundColor: accent }}
                                    >
                                        {getInitials(name)}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#1D2C7B]">
                                        {name}
                                    </span>
                                    <span className="shrink-0 text-xs font-semibold text-slate-500 tabular-nums">
                                        {group.items.length}
                                    </span>
                                </div>
                                <ul className="m-0 list-none divide-y divide-slate-100 p-0">
                                    {group.items.map((ev) => (
                                        <li key={ev.id}>
                                            <button
                                                type="button"
                                                className="w-full px-2.5 py-2.5 text-left transition-colors hover:bg-slate-50"
                                                onClick={() => {
                                                    onEventClick(ev);
                                                    onClose();
                                                }}
                                            >
                                                <span className="block truncate text-sm font-medium text-[#1D2C7B]">
                                                    {ev.title}
                                                </span>
                                                {ev.phone && (
                                                    <span className="mt-0.5 block text-xs text-slate-500 tabular-nums">
                                                        {ev.phone}
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}
        </Modal>
    );
}
